import { format } from 'date-fns';
import { FC } from 'react';
import { useTheme } from 'styled-components/macro';

import NotificationsHistoryLoader from './components/NotificationsHistoryLoader/NotificationsHistoryLoader';
import useNotificationsHistory from './hooks/useNotificationsHistory';
import {
  EmailReadIconWrapStyled,
  FilterTextStyled,
  FilterWrapStyled,
  MarkAsReadBtnStyled,
  MarkAsReadWrapStyled,
  MessageWrapStyled,
  NotificationButtonStyled,
  NotificationButtonWrapStyled,
  NotificationContentStyled,
  NotificationContentWrapStyled,
  NotificationRoNumberStyled,
  NotificationSentDateStyled,
  NotificationsAmountWrapStyled,
  NotificationsListItemStyled,
  NotificationsListStyled,
  NotificationsLoaderStyled,
  NotificationsWrapStyled,
  PopupHeaderStyled,
  ToggleWrapStyled,
  getPopupContentStyle,
} from './NotificationsHistory.styled';
import Avatar from 'components/Avatar/Avatar';
import ButtonIcon from 'components/ButtonIcon/ButtonIcon';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Popup from 'components/Popup/Popup';
import Toggle from 'components/Toggle/Toggle';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { getUserPlaceholderName } from 'helpers/user';
import { EmailUnreadIcon, NotificationIcon } from 'icons';

const NotificationsHistory: FC = () => {
  /**
   * Hooks
   */
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const {
    notificationsHistory,
    isLoadingNotificationsHistory,
    hasUnreadNotifications,
    unreadNotificationsAmount,
    isPopupOpened,
    showUnreadNotifications,
    handleNotificationContentClick,
    markNotificationAsRead,
    markNotificationsAsRead,
    handleOpenPopup,
    handleClosePopup,
    toggleUnreadFilter,
  } = useNotificationsHistory();

  /**
   * Variables
   */
  const notificationBtn = (
    <NotificationButtonWrapStyled onClick={handleOpenPopup} title="Show notifications">
      {hasUnreadNotifications && (
        <NotificationsAmountWrapStyled>
          <Typography size="xs" color="light">
            {unreadNotificationsAmount}
          </Typography>
        </NotificationsAmountWrapStyled>
      )}
      <NotificationButtonStyled variant="contained" icon={<NotificationIcon color="darkBlue" />} />
    </NotificationButtonWrapStyled>
  );
  const popupProps = isMobile
    ? {
        isOpen: isPopupOpened,
        showCloseBtn: true,
        popupId: ScrollLockIdE.NotificationsHistoryPopup,
        onClose: handleClosePopup,
      }
    : {
        position: 'bottom right',
        trigger: notificationBtn,
      };
  const showNoUnreadNotificationsMsg =
    !isLoadingNotificationsHistory && showUnreadNotifications && !notificationsHistory?.length;
  const showNotifications = !isLoadingNotificationsHistory && !!notificationsHistory?.length;

  return (
    <NotificationsWrapStyled className="notifications">
      {isMobile && notificationBtn}

      <Popup
        nested
        // @ts-expect-error: ignore overflowY css property error
        contentStyle={getPopupContentStyle(theme, isMobile)}
        closeOnEscape={false}
        {...popupProps}
      >
        <PopupHeaderStyled>
          <Typography variant="h2" color="dark">
            Notifications
          </Typography>
          <FilterWrapStyled>
            <FilterTextStyled size="md">Only unread</FilterTextStyled>
            <ToggleWrapStyled>
              <Toggle name="filterNotifications" onChange={toggleUnreadFilter} />
            </ToggleWrapStyled>
          </FilterWrapStyled>
          {hasUnreadNotifications && (
            <MarkAsReadWrapStyled>
              <MarkAsReadBtnStyled variant="text" onClick={markNotificationsAsRead}>
                Mark all as read
              </MarkAsReadBtnStyled>
            </MarkAsReadWrapStyled>
          )}
        </PopupHeaderStyled>

        {isLoadingNotificationsHistory && (
          <NotificationsLoaderStyled>
            <NotificationsHistoryLoader />
          </NotificationsLoaderStyled>
        )}

        {showNotifications && (
          <NotificationsListStyled>
            {notificationsHistory.map(({ id, sender, action, ticket, sentDate, isRead }) => {
              const [senderFirstName, senderLastName] = sender.displayName
                ? sender.displayName.split(' ')
                : [];

              return (
                <NotificationsListItemStyled key={id}>
                  <FlexContainer justify="flex-start">
                    <Avatar
                      size="md"
                      placeholder={getUserPlaceholderName(senderFirstName, senderLastName)}
                      imageUrl={sender.photoUrl}
                    />
                    <NotificationContentWrapStyled justify="between">
                      <NotificationContentStyled
                        direction="column"
                        align="start"
                        onClick={handleNotificationContentClick({
                          ticketId: ticket?.id,
                          notificationId: id,
                        })}
                      >
                        <NotificationSentDateStyled size="xs" weight={isRead ? 'normal' : 'bold'}>
                          {format(new Date(sentDate), 'MMM d, yyyy, p')}
                        </NotificationSentDateStyled>
                        <Typography size="md" weight={isRead ? 'normal' : 'bold'}>
                          {sender.displayName} {action}
                        </Typography>
                        <NotificationRoNumberStyled size="md" weight={isRead ? 'normal' : 'bold'}>
                          Ticket RO Number: {ticket?.roNumber}
                        </NotificationRoNumberStyled>
                      </NotificationContentStyled>
                      {!isRead && (
                        <ButtonIcon
                          size="md"
                          title="Mark notification as read"
                          icon={
                            <EmailReadIconWrapStyled>
                              <EmailUnreadIcon />
                            </EmailReadIconWrapStyled>
                          }
                          onClick={markNotificationAsRead(id)}
                        />
                      )}
                    </NotificationContentWrapStyled>
                  </FlexContainer>
                </NotificationsListItemStyled>
              );
            })}
          </NotificationsListStyled>
        )}

        {showNoUnreadNotificationsMsg && (
          <MessageWrapStyled>
            <Typography variant="h3" color="darkBlue" align="center">
              You've read all your notifications from the last 30 days
            </Typography>
          </MessageWrapStyled>
        )}
      </Popup>
    </NotificationsWrapStyled>
  );
};

export default NotificationsHistory;
